// Utility composables shared by the dialog, toast, and menu systems.
// See docs/framework/ui/composables.md.

import { onBeforeUnmount, onMounted, watchEffect, type Ref } from 'vue'

// ---------------------------------------------------------------- Focus trap

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((el) => !el.hasAttribute('inert') && el.offsetParent !== null)
}

export interface FocusTrapOptions {
  /** Reactive predicate. Trap engages when true, releases when false. */
  enabled: () => boolean
  /** Element to focus when the trap first activates, if not the first focusable. */
  initialFocus?: () => HTMLElement | null
}

/**
 * Traps keyboard focus inside a DOM subtree. Focus is captured on first
 * activation, moved into the subtree, and restored to the original element on
 * component unmount. Toggling `enabled` off and on again re-focuses the first
 * focusable but does not re-capture the restore target — this matches nested
 * dialog behaviour where a parent's trap deactivates when a child opens and
 * needs to grab focus back when the child closes.
 */
export function useFocusTrap(
  rootRef: Ref<HTMLElement | null>,
  options: FocusTrapOptions,
): void {
  let restoreTarget: HTMLElement | null = null
  let listenerAttached = false
  let hasBeenActivated = false

  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Tab') return
    const root = rootRef.value
    if (!root) return
    const items = focusables(root)
    if (items.length === 0) { e.preventDefault(); return }

    const first = items[0]!
    const last  = items[items.length - 1]!
    const activeEl = document.activeElement as HTMLElement | null

    if (e.shiftKey && activeEl === first) {
      e.preventDefault(); last.focus()
    } else if (!e.shiftKey && activeEl === last) {
      e.preventDefault(); first.focus()
    }
  }

  function activate() {
    if (listenerAttached) return
    if (!hasBeenActivated) {
      restoreTarget = document.activeElement as HTMLElement | null
      hasBeenActivated = true
    }
    document.addEventListener('keydown', onKeydown, true)
    listenerAttached = true

    // Focus after mount is settled.
    queueMicrotask(() => {
      const root = rootRef.value
      if (!root) return
      const initial = options.initialFocus?.() ?? focusables(root)[0] ?? root
      initial.focus()
    })
  }

  function deactivate() {
    if (!listenerAttached) return
    document.removeEventListener('keydown', onKeydown, true)
    listenerAttached = false
  }

  onMounted(() => {
    watchEffect(() => (options.enabled() ? activate() : deactivate()))
  })

  onBeforeUnmount(() => {
    deactivate()
    restoreTarget?.focus?.()
    restoreTarget = null
    hasBeenActivated = false
  })
}

// -------------------------------------------------------------- Scroll lock

let scrollLockCount = 0
let savedOverflow:     string | null = null
let savedPaddingRight: string | null = null

function acquireScrollLock(): void {
  scrollLockCount++
  if (scrollLockCount !== 1) return
  const body = document.body
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
  savedOverflow     = body.style.overflow
  savedPaddingRight = body.style.paddingRight
  body.style.overflow = 'hidden'
  if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
}

function releaseScrollLock(): void {
  if (scrollLockCount === 0) return
  scrollLockCount--
  if (scrollLockCount !== 0) return
  const body = document.body
  body.style.overflow     = savedOverflow     ?? ''
  body.style.paddingRight = savedPaddingRight ?? ''
  savedOverflow = null
  savedPaddingRight = null
}

export interface BodyScrollLockOptions {
  enabled?: () => boolean
}

/**
 * Refcounted body-scroll lock. Two callers may lock simultaneously; the body
 * unlocks only when both release. Pass `enabled` to auto-manage from a
 * reactive predicate; call `acquire`/`release` yourself otherwise.
 */
export function useBodyScrollLock(
  options: BodyScrollLockOptions = {},
): { acquire: () => void; release: () => void } {
  if (options.enabled) {
    let held = false
    onMounted(() => {
      watchEffect(() => {
        const want = options.enabled!()
        if (want && !held) { acquireScrollLock(); held = true }
        else if (!want && held) { releaseScrollLock(); held = false }
      })
    })
    onBeforeUnmount(() => {
      if (held) { releaseScrollLock(); held = false }
    })
  }
  return { acquire: acquireScrollLock, release: releaseScrollLock }
}

// -------------------------------------------------------------------- Keydown

export interface KeyChord {
  key:    string
  ctrl?:  boolean
  meta?:  boolean
  shift?: boolean
  alt?:   boolean
}

export interface KeydownOptions {
  when?:            () => boolean
  preventDefault?:  boolean
  capture?:         boolean
  target?:          EventTarget
}

/**
 * Attach a document-level keydown listener for a specific key or chord.
 * Auto-cleaned up on unmount. `when` gates handler execution without
 * unregistering the listener.
 */
export function onKeydown(
  keyOrChord: string | KeyChord,
  handler: (e: KeyboardEvent) => void,
  options: KeydownOptions = {},
): void {
  const chord: KeyChord = typeof keyOrChord === 'string' ? { key: keyOrChord } : keyOrChord
  const target  = options.target  ?? document
  const capture = options.capture ?? false

  function listener(e: Event) {
    const kb = e as KeyboardEvent
    if (kb.key !== chord.key) return
    if (chord.ctrl  != null && kb.ctrlKey  !== chord.ctrl)  return
    if (chord.meta  != null && kb.metaKey  !== chord.meta)  return
    if (chord.shift != null && kb.shiftKey !== chord.shift) return
    if (chord.alt   != null && kb.altKey   !== chord.alt)   return
    if (options.when && !options.when()) return
    if (options.preventDefault) kb.preventDefault()
    handler(kb)
  }

  onMounted(() => target.addEventListener('keydown', listener, capture))
  onBeforeUnmount(() => target.removeEventListener('keydown', listener, capture))
}
